import * as React from 'react'

interface VenueOption {
  id: string
  name: string
}

interface VenueAvailabilityPickerProps {
  /** The venues the tenant owns. */
  venues: VenueOption[]
  /** Currently-selected venue ids (empty = available nowhere yet). */
  value: string[]
  /** Called with the full new set whenever a box is toggled or all/none is used. */
  onChange: (venueIds: string[]) => void
  disabled?: boolean
  /** Copy when nothing is selected (default: furniture availability phrasing). */
  emptyLabel?: string
  /** Copy when some are selected; receives selected count and total. */
  summaryLabel?: (selected: number, total: number) => string
  /** Empty-tenant message (default: furniture-oriented). */
  noVenuesLabel?: string
}

/**
 * Multi-select venue checklist: one checkbox per venue, plus select-all /
 * clear. Used for furniture availability and venue-admin venue assignments.
 */
export function VenueAvailabilityPicker({
  venues,
  value,
  onChange,
  disabled,
  emptyLabel = 'Not available at any venue yet',
  summaryLabel = (selected, total) => `Available at ${selected} of ${total}`,
  noVenuesLabel = 'No venues yet — create a venue to make furniture available.',
}: VenueAvailabilityPickerProps) {
  const selected = React.useMemo(() => new Set(value), [value])
  const allSelected = venues.length > 0 && venues.every((v) => selected.has(v.id))

  function toggle(id: string): void {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange(venues.filter((v) => next.has(v.id)).map((v) => v.id))
  }

  if (venues.length === 0) {
    return <p className="text-sm text-ink-muted">{noVenuesLabel}</p>
  }

  return (
    <div className="overflow-hidden rounded-md border border-line">
      <div className="flex items-center justify-between border-b border-line px-3 py-2">
        <span className="text-xs text-ink-muted">
          {selected.size === 0 ? emptyLabel : summaryLabel(selected.size, venues.length)}
        </span>
        <div className="flex items-center gap-3 text-xs">
          <button
            type="button"
            disabled={disabled || allSelected}
            onClick={() => onChange(venues.map((v) => v.id))}
            className="text-ink-muted transition-colors hover:text-ink disabled:pointer-events-none disabled:opacity-40"
          >
            Select all
          </button>
          <button
            type="button"
            disabled={disabled || selected.size === 0}
            onClick={() => onChange([])}
            className="text-ink-muted transition-colors hover:text-ink disabled:pointer-events-none disabled:opacity-40"
          >
            Clear
          </button>
        </div>
      </div>
      <ul className="divide-y divide-line">
        {venues.map((v) => (
          <li key={v.id}>
            <label className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm transition-colors hover:bg-surface-hover">
              <input
                type="checkbox"
                checked={selected.has(v.id)}
                disabled={disabled}
                onChange={() => toggle(v.id)}
              />
              <span className="min-w-0 flex-1 truncate">{v.name}</span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  )
}
